import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiClient } from '@core/network/apiClient';
import { fetchAllPages } from '@core/network/paginate';
import type { Dashboard, DomainCatalogItem } from '@features/dashboard/types/dashboard.types';
import type { MyProjectItem } from '@features/projects/types/project.types';

export const dashboardKeys = {
  all: ['dashboard'] as const,
  summary: () => [...dashboardKeys.all, 'summary'] as const,
  myProjects: () => [...dashboardKeys.all, 'my-projects'] as const,
  catalog: () => [...dashboardKeys.all, 'catalog'] as const,
};

/** Everything the landing screen needs, in one call. */
export function useDashboard(): UseQueryResult<Dashboard> {
  return useQuery({
    queryKey: dashboardKeys.summary(),
    queryFn: async () => {
      const { data } = await apiClient.get<Dashboard>('/me/dashboard');
      return data;
    },
  });
}

/** The caller's projects, each with their OWN sitting count — the rail's top
 *  level, and half of what the composer searches. Held for the same reason
 *  `useMySessions` is: the rail asks for it on every route. */
export function useMyProjects(): UseQueryResult<MyProjectItem[]> {
  return useQuery({
    queryKey: dashboardKeys.myProjects(),
    queryFn: () => fetchAllPages<MyProjectItem>(apiClient, '/me/projects'),
    staleTime: 5 * 60_000,
  });
}

/**
 * Every live domain, as offered at project-creation time. Deliberately wider
 * than `/domains`: an expert with no grants must still be able to start.
 *
 * Exported as options rather than only as a hook because the command bar needs
 * this list ONCE, at the moment someone asks to start a project, and must not
 * subscribe the home screen to a request most visits never need.
 */
export const domainCatalogQuery = {
  queryKey: dashboardKeys.catalog(),
  queryFn: () => fetchAllPages<DomainCatalogItem>(apiClient, '/domains/catalog'),
};

export function useDomainCatalog(): UseQueryResult<DomainCatalogItem[]> {
  return useQuery(domainCatalogQuery);
}
